/*
 * store-availability-mini.js
 * ----------------------------------------------------------------------------
 * Pastille « Disponible / Indisponible à {magasin favori} » sur les cartes
 * produit (collections, carrousels home…).
 *
 * Historique : ce script vivait INLINE dans snippets/compact-store-collection.liquid,
 * donc ré-émis à l'identique pour CHAQUE carte produit (~6 Ko × N produits →
 * ~300 Ko de JS dupliqué dans le HTML de la home). Il est désormais externalisé
 * et chargé UNE SEULE FOIS (defer) via layout/theme.liquid.
 *
 * Le snippet ne rend plus que la donnée par variante :
 *   <div id="StoreAvailabilityMini-{id}" data-locs='[...]'>…</div>
 * Ce script scanne tous ces noeuds (applyAllIn) et reste idempotent grâce aux
 * garde-fous globaux window.__storeFavPromise / window.__storeAvailMiniObserver,
 * d'où le fait qu'une instance unique suffit pour toute la page.
 * ----------------------------------------------------------------------------
 */
(function(){
  const VALID_SLUGS = ['decathlon-saint-denis','decathlon-saint-pierre','decathlon-sainte-suzanne'];
  const SLUG_TO_NAME = {
    'decathlon-saint-denis':'Decathlon Saint-Denis',
    'decathlon-saint-pierre':'Decathlon Saint-Pierre',
    'decathlon-sainte-suzanne':'Decathlon Sainte-Suzanne'
  };
  /* noms abrégés pour le fit */
  const SLUG_TO_SHORT = {
    'decathlon-saint-denis':'Saint-Denis',
    'decathlon-saint-pierre':'Saint-Pierre',
    'decathlon-sainte-suzanne':'Sainte-Suzanne'
  };
  const SLUG_TO_MINI = {
    'decathlon-saint-denis':'Saint-Denis',
    'decathlon-saint-pierre':'Saint-Pierre',
    'decathlon-sainte-suzanne':'Ste-Suzanne'
  };

  // -------- Favori (panier/LS) --------
  async function getFavoriteOnce(){
    if (!window.__storeFavPromise) {
      window.__storeFavPromise = (async () => {
        let slug=null, name=null;
        try {
          const cart = await fetch('/cart.js',{credentials:'same-origin'}).then(r=>r.json());
          slug = cart?.attributes?.favorite_store_slug || null;
          name = cart?.attributes?.favorite_store_name || null;
        } catch(_){}
        if (!slug) {
          try {
            slug = localStorage.getItem('favorite_store_slug');
            name = localStorage.getItem('favorite_store_name');
          } catch(_){}
        }
        if (slug && VALID_SLUGS.includes(slug)) {
          return {slug, name: name || SLUG_TO_NAME[slug] || slug};
        }
        return null; // aucun favori
      })();
    }
    return window.__storeFavPromise;
  }

  function parseLocs(el){
    // data-locs est HTML-escaped par Liquid, mais getAttribute() renvoie du texte décodé → JSON OK
    const raw = el.getAttribute('data-locs') || '[]';
    try { return JSON.parse(raw); } catch(e){ return []; }
  }

  /* ---- helpers fit ---- */
  function isOverflowing(node){ return node && (node.scrollWidth - node.clientWidth > 1); }
  function computeNames(fav){
    const longName  = SLUG_TO_NAME[fav.slug]  || fav.name || fav.slug;
    const shortName = SLUG_TO_SHORT[fav.slug] || (longName||'').replace(/^Decathlon\s+/i,'');
    const miniName  = SLUG_TO_MINI[fav.slug]  || shortName.replace(/\bSaint\b/i,'Saint').replace(/\bSainte\b/i,'Ste');
    return {longName, shortName, miniName};
  }

  /* -------- Rendu (avec fit 3 paliers) -------- */
  function renderMini(el, fav){
    if (!el || !fav) { if (el) el.style.display='none'; return; }
    const entries = parseLocs(el);
    const entry = entries.find(e => e && e.slug === fav.slug);
    const ok = !!(entry && entry.available);

    el.style.display = 'inline-flex';
    const iconIn  = el.querySelector('.icon-in');
    const iconOut = el.querySelector('.icon-out');
    const text    = el.querySelector('.js-mini-text');

    if (iconIn && iconOut){
      iconIn.style.display  = ok ? 'inline-block' : 'none';
      iconOut.style.display = ok ? 'none' : 'inline-block';
    }
    if (!text) return;

    const {longName, shortName, miniName} = computeNames(fav);
    const FULL  = ok ? `Disponible à ${longName}`    : `Indisponible à ${longName}`;
    const SHORT = ok ? `Disponible • ${shortName}`    : `Indisponible • ${miniName}`;
    const MINI  = ok ? `Disponible • ${miniName}`                : `Indisponible • ${miniName}`;
    const fullTitle = ok ? `Disponible à ${longName}` : `Indisponible à ${longName}`;

    // base: no-wrap pour mesurer, titre/aria complets pour l’accessibilité
    text.style.whiteSpace = 'nowrap';
    text.textContent = FULL;
    text.title = fullTitle;
    el.setAttribute('aria-label', fullTitle);

    // si ça déborde → version courte, sinon → mini
    if (isOverflowing(text)){
      text.textContent = SHORT;
      if (isOverflowing(text)){
        text.textContent = MINI;
      }
    }

    // s’adapte aux changements de largeur
    if (!el.__ro && 'ResizeObserver' in window){
      el.__ro = new ResizeObserver(() => {
        text.textContent = FULL;
        if (isOverflowing(text)){
          text.textContent = SHORT;
          if (isOverflowing(text)){
            text.textContent = MINI;
          }
        }
      });
      el.__ro.observe(el);
    }
  }

  async function applyAllIn(root=document){
    const fav = await getFavoriteOnce();
    const nodes = root.querySelectorAll('[id^="StoreAvailabilityMini-"]');
    nodes.forEach(el => renderMini(el, fav));
  }

  // ---- Boot + Re-renders
  function setupObservers(){
    if (window.__storeAvailMiniObserver) return;
    // 1) MutationObserver : repère les ajouts de cartes dans la collection (reload partiels, facettes, pagination)
    const mo = new MutationObserver((muts)=>{
      let need=false;
      for (const m of muts){
        for (const n of m.addedNodes || []){
          if (!(n instanceof Element)) continue;
          if (n.matches?.('[id^="StoreAvailabilityMini-"]') || n.querySelector?.('[id^="StoreAvailabilityMini-"]')) {
            need = true; break;
          }
        }
        if (need) break;
      }
      if (need) {
        // petite détente pour laisser le thème finir le DOM
        requestAnimationFrame(()=> applyAllIn(document));
      }
    });
    mo.observe(document.body, {childList:true, subtree:true});
    window.__storeAvailMiniObserver = mo;

    // 2) Shopify / thèmes : certains déclenchent ces events lors des rafraîchissements de sections
    document.addEventListener('shopify:section:load',   ()=> applyAllIn(document));
    document.addEventListener('shopify:section:select', ()=> applyAllIn(document));
    document.addEventListener('shopify:section:deselect',()=> applyAllIn(document));

    // 3) Changement de favori (événement déjà utilisé ailleurs dans ton projet)
    window.addEventListener('favorite-store:changed', async ()=>{
      window.__storeFavPromise = null; // invalide le cache
      await applyAllIn(document);
    });
  }

  async function boot(){
    setupObservers();
    await applyAllIn(document);
  }

  // Lancer immédiatement si le DOM est déjà prêt, sinon attendre l’événement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
